import { CallPip } from '@/components/call-bar/call-pip';
import { PipSessionPanel } from '@/components/call-bar/pip-session-panel';
import { Icon, Icons } from '@/components/icon';
import type { PipCallTag } from '@/electron/bridge/pip-call';
import { isDesktop } from '@/lib/platform';
import type { VoiceCallStateStatus } from '@packages/client-sdk-react/src/voice/voice-call-container';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

export const Route = createFileRoute('/desktop/pip-call')({
  component: PipCallWindow,
});

type PipSessionContact = {
  id: string;
  name?: string;
  lastName?: string;
  phoneNumber?: string;
  jobTitle?: string;
  companyName?: string;
  dialingState: 'pending' | 'ringing' | 'connected' | 'no-answer' | 'voicemail';
  imageUrl?: string;
};

type PipSessionData = {
  isActive: boolean;
  contacts: PipSessionContact[];
  activeContactId?: string;
  completedCount: number;
  totalCount: number;
  minutesLeft: number;
};

interface PipCallData {
  callId?: string;
  contactName?: string;
  contactPhoneNumber?: string;
  contactCompanyName?: string;
  contactJobTitle?: string;
  callStatus: VoiceCallStateStatus;
  isMuted: boolean;
  duration: number;
  direction: 'incoming' | 'outgoing' | 'unknown';
  userLine?: {
    lineName?: string;
    lineNumber?: string;
  };
  extra?: {
    summary?: string;
  };
  microphoneDeviceId?: string;
  declineState?:
    | { state: 'voicemail' }
    | { state: 'ai-receptionist' }
    | { state: 'call-transfer'; extra: string }
    | { state: 'unknown' };
  session?: PipSessionData;
  callTags?: {
    tags: PipCallTag[];
    selectedTagKeys: string[];
  };
}

function PipCallWindow() {
  const [callData, setCallData] = useState<PipCallData | null>(null);
  const [sessionData, setSessionData] = useState<PipSessionData | null>(null);
  const [showDeclinedState, setShowDeclinedState] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const cleanupData = window.pipCallWindowAPI?.onData((data) => {
      const nextData = data as PipCallData;
      setCallData(nextData);
      if (nextData.session?.isActive) {
        setSessionData(nextData.session);
      }
    });
    const cleanupSessionData = window.pipCallWindowAPI?.onSessionData((data) => {
      setSessionData(data ?? null);
    });

    window.pipCallWindowAPI?.requestData();

    return () => {
      cleanupData?.();
      cleanupSessionData?.();
    };
  }, []);

  // Reset declined state when a new incoming call arrives (PIP window reuse)
  useEffect(() => {
    if (callData?.callStatus === 'ringing') {
      setShowDeclinedState(false);
    }
  }, [callData?.callStatus]);

  if (isDesktop() === false) {
    return null;
  }

  if (!callData) {
    return null;
  }

  const contact =
    callData.contactName || callData.contactPhoneNumber
      ? {
          name: callData.contactName,
          phoneNumber: callData.contactPhoneNumber,
          companyName: callData.contactCompanyName,
          jobTitle: callData.contactJobTitle,
        }
      : undefined;

  const userLine = callData.userLine
    ? {
        lineName: callData.userLine.lineName,
        lineNumber: callData.userLine.lineNumber,
      }
    : undefined;

  const extra = callData.extra
    ? {
        summary: callData.extra.summary,
      }
    : undefined;

  const hasSession = sessionData?.isActive;

  return (
    <div
      className="flex h-screen w-screen flex-col overflow-hidden"
      style={{ WebkitAppRegion: 'drag' } as React.CSSProperties}
    >
      {/* Header + Call UI — fixed at top */}
      <div className={hasSession ? 'shrink-0' : 'flex min-h-0 flex-1 flex-col'}>
        <div className="flex flex-row items-center justify-between px-3 py-2">
          <button
            type="button"
            onClick={() => {
              window.pipCallWindowAPI?.focusMain();
              window.pipCallWindowAPI?.hide();
            }}
            className="size-6 cursor-pointer rounded-md border border-primary-10 hover:bg-primary-3 z-raised focus:outline-none"
            style={{ WebkitAppRegion: 'no-drag' } as React.CSSProperties}
          >
            <Icon name={Icons.ArrowUpLeft} className="text-sm" />
          </button>

          <Icon name={Icons.DotsSixHorizontal} className="text-sm self-start" />

          <button
            type="button"
            onClick={() => window.pipCallWindowAPI?.hide()}
            className="size-6 cursor-pointer rounded-md border border-primary-10 hover:bg-primary-3 z-raised focus:outline-none"
            style={{ WebkitAppRegion: 'no-drag' } as React.CSSProperties}
          >
            <Icon name={Icons.Cancel} className="text-sm" />
          </button>
        </div>

        <CallPip
          className={hasSession ? 'h-[296px] flex-none' : 'min-h-0 flex-1'}
          callId={callData.callId}
          contact={contact}
          callStatus={callData.callStatus}
          isMuted={callData.isMuted}
          duration={callData.duration}
          direction={callData.direction}
          showDeclinedState={showDeclinedState}
          declineState={callData.declineState}
          userLine={userLine}
          extra={extra}
          microphoneDeviceId={callData.microphoneDeviceId}
          callTags={
            callData.callTags
              ? {
                  tags: callData.callTags.tags,
                  selectedTagKeys: callData.callTags.selectedTagKeys,
                  onAddTag: (key) => window.pipCallWindowAPI?.addTag(key),
                  onRemoveTag: (key) => window.pipCallWindowAPI?.removeTag(key),
                }
              : undefined
          }
          onToggleMute={() => {
            window.pipCallWindowAPI?.toggleMute();
          }}
          onAnswer={async () => {
            return (await window.pipCallWindowAPI?.answer()) ?? false;
          }}
          onDecline={() => {
            setShowDeclinedState(true);
            window.pipCallWindowAPI?.decline();
          }}
          onHangup={() => {
            window.pipCallWindowAPI?.hangup();
          }}
          onContactClick={() => {
            window.pipCallWindowAPI?.navigateToContact();
          }}
        />
      </div>

      {/* Power Dialer session panel — fills remaining space, scrolls internally */}
      {hasSession && (
        <div className="min-h-0 flex-1 overflow-hidden">
          <PipSessionPanel
            session={sessionData}
            onEndSession={() => {
              window.pipCallWindowAPI?.endSession();
            }}
            onToggleList={(expanded) => {
              window.pipCallWindowAPI?.setSessionListExpanded(expanded);
            }}
            windowWidth={windowWidth}
          />
        </div>
      )}
    </div>
  );
}
